import React, { useState } from 'react';
import { omit, set, get } from 'lodash';
import styled from 'styled-components';
import { PhoneNumberUtil, PhoneNumberFormat as PNF } from 'google-libphonenumber';
import { useDispatch } from 'react-redux';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import smsConfigurationResource from 'src/actions/channels/smsConfigurationResource';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import Footer from 'src/presentation/components/forms/Footer';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import Input from 'src/presentation/components/forms/Input';
import Checkbox from 'src/presentation/components/forms/Checkbox';
import { InputSize } from 'src/presentation/components/forms/InputSize';
import ActivateMessageAutomationModal from 'src/presentation/components/common/ActivateMessageAutomationModal';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import { TargetTypeAgent, TargetTypeAutomation } from 'src/models/MessageAutomationConfiguration';
const dataAidPrefix = 'admin-sms-';
const phoneUtil = PhoneNumberUtil.getInstance();

const Description = ({ phoneNumber }) => {
  return (
    <FormDescription data-aid={`${dataAidPrefix}editor-description`}>
      Modify your SMS configuration for <b>{phoneNumber}</b>. Review how to{' '}
      <ExternalLink
        data-aid={`-set-up-sms`}
        label="manage phone numbers."
        to="https://help.gladly.com/implementation/docs/set-up-sms"
      />
    </FormDescription>
  );
};

const SmsEditor = ({ errors, item, root }) => {
  const dispatch = useDispatch();
  const isFeaturedEnabled = useIsFeatureEnabled();
  const [showMsgAutomationConfirmationModal, setShowMsgAutomationConfirmationModal] = useState<boolean>(false);
  const updateSmsConfiguration = smsConfigurationResource.effects.update;
  const formattedPhoneNumber = getFormattedPhoneNumber(item.number);

  const formContext = useForm({
    defaultItem: { internalName: '', messageAutomationConfiguration: undefined },
    errors,
    item,
    resetErrors: smsConfigurationResource.actions.resetErrors,
    root,
    onSave: ({ resetErrors, workingState }) => {
      dispatch(resetErrors());
      const newState = omit(workingState, ['inbox']);
      dispatch(
        updateSmsConfiguration(newState, newState, {
          customToastMsg: `Updated SMS configuration for "${formattedPhoneNumber}"`,
        })
      );
    },
    validate: smsConfigurationResource.effects.validate,
  });

  const { onClose, onSave, workingState, updateState } = formContext;

  const automationMessagingTargetType = get(workingState, 'messageAutomationConfiguration.targetType');
  const automationMessagingEnabled = automationMessagingTargetType === TargetTypeAutomation;
  const toggleAutomatedMessagingTargetType = ({ checked }) => {
    if (checked) {
      setShowMsgAutomationConfirmationModal(true);
    } else {
      const changes = set({}, 'messageAutomationConfiguration.targetType', TargetTypeAgent);
      updateState(changes, { replaceArrayValues: true });
    }
  };

  const onProceedWithMessageAutomation = () => {
    const changes = set({}, 'messageAutomationConfiguration.targetType', TargetTypeAutomation);
    updateState(changes, { replaceArrayValues: true });
    setShowMsgAutomationConfirmationModal(false);
  };

  return (
    <Form context={formContext}>
      {showMsgAutomationConfirmationModal ? (
        <ActivateMessageAutomationModal
          onCancel={() => setShowMsgAutomationConfirmationModal(false)}
          onSubmit={onProceedWithMessageAutomation}
          title={`Enable ${formattedPhoneNumber} for Automation Services`}
        />
      ) : null}
      <H1>Edit SMS configuration</H1>
      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section description={<Description phoneNumber={formattedPhoneNumber} />}>
          <Row>
            <Input
              data-aid={`${dataAidPrefix}editor-name-input`}
              description="A nickname that makes it easier to identify this Entry Point at a glance."
              attr="name"
              autoFocus
              label="Internal name"
              placeholder="Internal name"
            />
          </Row>
          {isFeaturedEnabled('smsMessageAutomation') ? (
            <Row>
              <CheckboxContainer>
                <Checkbox
                  checked={automationMessagingEnabled}
                  label={<CheckboxLabel label={`Enable ${formattedPhoneNumber} for Automation Services`} />}
                  attr="enable-automation-messaging"
                  data-aid={`${dataAidPrefix}enable-automation-messaging-checkbox`}
                  onChange={() => toggleAutomatedMessagingTargetType({ checked: !automationMessagingEnabled })}
                  value={automationMessagingEnabled ? 'checked' : ''}
                />
                <CheckboxInfo>
                  When the checkbox is selected, Gladly Sidekick and 3rd party automation services will have access to
                  this phone number for the purpose of communication and routing.
                </CheckboxInfo>
              </CheckboxContainer>
            </Row>
          ) : null}
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const CheckboxLabel = ({ label }) => {
  return <CheckboxLabelText>{label}</CheckboxLabelText>;
};

const CheckboxContainer = styled.span`
  width: ${InputSize.XLARGE};
`;
const CheckboxLabelText = styled.span`
  font-weight: bold;
  display: flex;
`;
const CheckboxInfo = styled.span`
  display: flex;
  line-height: 1.6;
  padding-left: 24px;
  position: relative;
  top: 5px;
  color: ${p => p.theme.colors.gray700};
`;

const FormDescription = styled.div``;

function getFormattedPhoneNumber(number) {
  try {
    return phoneUtil.format(phoneUtil.parse(number), PNF.INTERNATIONAL);
  } catch (e) {
    // a short code
    return phoneUtil.format(phoneUtil.parse(number, 'US'), PNF.DOMESTIC);
  }
  return '';
}

export default SmsEditor;
